import { Inject, Injectable } from '@angular/core';
import { DwMediaRepositoryService, DwOrmDataService, DwOrmDataServiceToken } from '@devwareapps/devware-cap';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppMetaData, QuestionEntity, QuestionStatusAllItems, QuestionTypeAllItems } from '../../../meta-data/app-meta-data.service';
import { QuestionKind, ScenarioQuestion } from '../models/scenario.model';

export interface BankQuestionItem {
  id: number;
  typeLabel: string;
  typeKey: 'TF' | 'MC' | 'MA';
  source: string;
  text: string;
  answerCount: number;
  /** Kinds this bank question can be rendered as without re-authoring. */
  kinds: QuestionKind[];
  entity: QuestionEntity;
}

export interface BankPage {
  items: BankQuestionItem[];
  total: number;
  hasMore: boolean;
}

const KINDS_BY_TYPE: { [typeId: number]: QuestionKind[] } = {
  [QuestionTypeAllItems.TrueOrFalse]: ['true-false-round', 'timed', 'confidence'],
  [QuestionTypeAllItems.MultipleChoice]: ['multiple-choice', 'timed', 'confidence'],
  [QuestionTypeAllItems.MultipleAnswer]: ['multiple-response'],
};

@Injectable({ providedIn: 'root' })
export class ScenarioQuestionBankService {
  constructor(
    @Inject(DwOrmDataServiceToken) private readonly dwOrmDataService: DwOrmDataService,
    private readonly dwMediaRepository: DwMediaRepositoryService,
  ) { }

  search(term: string, typeId: number | null, pageNo: number, pageSize = 25): Observable<BankPage> {
    const query = AppMetaData.Question.CreateQueryBuilder();
    const search = (term || '').trim();

    query.query.FieldSettings.LoadAllLookupDisplayFields = true;
    query.setupPaging(pageNo, pageSize, true);
    // Legacy rows can have a null status; SQL NotEqual alone would drop them.
    query.addFilterAnd(f => f.GroupOr(
      g => g.IsBlank(a => a.QuestionStatusId, null),
      g => g.NotEqual(a => a.QuestionStatusId, QuestionStatusAllItems.Deleted),
    ));
    if (typeId) {
      query.addFilterAnd(f => f.Equal(a => a.QuestionTypeId, typeId));
    }
    if (search) {
      query.addFilterAnd(f => f.GroupOr(
        g => g.Contains(a => a.Question, search),
        g => g.Contains(a => a.QuestionName, search),
      ));
    }

    const answers = AppMetaData.QuestionAnswer.CreateQueryBuilder();
    answers.addOrderBy(a => a.AnswerOrder);
    query.addPrefetch(r => r.QuestionAnswer, answers.query);
    query.addPrefetch(r => r.DwRoMediaByQuestionImageMediaId);
    query.addOrderByDesc(a => a.ModifiedDateTime);

    return this.dwOrmDataService.executePagedQuery<QuestionEntity>(query).pipe(
      map(result => {
        const rows = result?.Data || [];
        const total = result?.PageInfo?.TotalRecordCount || rows.length;
        return {
          items: rows.map(q => this.toItem(q)),
          total,
          hasMore: result?.PageInfo ? pageNo < result.PageInfo.TotalPageCount : rows.length === pageSize,
        };
      }));
  }

  /** Maps a bank question into a scenario question of the requested kind. */
  toScenarioQuestion(entity: QuestionEntity, kind: QuestionKind): ScenarioQuestion {
    const answers = entity.QuestionAnswer || [];
    const isTf = entity.QuestionTypeId === QuestionTypeAllItems.TrueOrFalse;
    const tfAnswer = (entity.CorrectAnswer || '').toLowerCase() === 'true';
    const explanation = stripHtml(entity.CorrectAnswerNotes) || answers.map(a => stripHtml(a.ExplanationText)).filter(Boolean)[0] || '';
    const media = entity.QuestionImageDwroMedia?.MediaPath;
    const text = stripHtml(entity.Question) || entity.QuestionName || 'Untitled question';
    const choices = isTf
      ? [{ text: 'True', correct: tfAnswer }, { text: 'False', correct: !tfAnswer }]
      : answers.map(a => ({ text: stripHtml(a.AnswerText), correct: !!a.IsCorrectAnswer, why: stripHtml(a.ExplanationText) }));

    const base: ScenarioQuestion = {
      kind, title: text, points: 5, explanation,
      bankQuestionId: entity.QuestionId,
      bankSource: entity.CourseModuleIdDisplay || entity.CourseIdDisplay || 'Quiz bank',
      imageUrl: media ? `${this.dwMediaRepository.mediaApi}/download/${media}` : undefined,
    };

    if (kind === 'true-false-round') {
      return { ...base, title: entity.QuestionName || 'True or false?', statements: [{ text, answer: tfAnswer }], points: 1 };
    }
    if (kind === 'timed') {
      return { ...base, choices, seconds: 20 };
    }
    return { ...base, choices };
  }

  private toItem(q: QuestionEntity): BankQuestionItem {
    const typeKey = q.QuestionTypeId === QuestionTypeAllItems.TrueOrFalse ? 'TF' : q.QuestionTypeId === QuestionTypeAllItems.MultipleAnswer ? 'MA' : 'MC';
    return {
      id: q.QuestionId as number,
      typeKey,
      typeLabel: q.QuestionTypeIdDisplay || { TF: 'True / False', MC: 'Multiple choice', MA: 'Multiple answer' }[typeKey],
      source: q.CourseModuleIdDisplay || q.CourseIdDisplay || 'Unassigned',
      text: stripHtml(q.Question) || q.QuestionName || '(no text)',
      answerCount: (q.QuestionAnswer || []).length,
      kinds: KINDS_BY_TYPE[q.QuestionTypeId as number] || ['multiple-choice'],
      entity: q,
    };
  }
}

function stripHtml(html: string | undefined): string {
  if (!html) { return ''; }
  // DOMParser builds an inert document: no image loads or handlers fire from bank HTML.
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  return text.replace(/\s+/g, ' ').trim();
}
